import {
  computed,
  signal,
  type Signal,
  type WritableSignal,
} from '@angular/core';
import {
  type IAppointment,
  type IInvoice,
} from '@principle-theorem/principle-core/interfaces';
import { type IAppointmentCreditState } from '@principle-theorem/ng-principle-shared';
import {
  type DocumentReference,
  type WithRef,
} from '@principle-theorem/shared';
import {
  AppointmentCreditInputs,
  AutoApplyEligibility,
  type AppointmentRefInput,
  type AutoApplyEnabledInput,
} from './appointment-credit-inputs';

export enum AutoApplyDepositStatus {
  Inactive = 'inactive',
  Resolving = 'resolving',
  Applying = 'applying',
  Applied = 'applied',
  Failed = 'failed',
}

export const RESOLVING_DEPOSIT_LABEL = 'Checking for booking deposit...';
export const APPLYING_DEPOSIT_LABEL = 'Applying booking deposit...';

export interface IAutoApplyAttempt {
  invoice: WithRef<IInvoice>;
  appointmentRef: DocumentReference<IAppointment>;
  creditState: IAppointmentCreditState;
}

export interface IAutoApplyGate {
  invoice: WithRef<IInvoice>;
  appointmentRef: AppointmentRefInput;
  autoApplyEnabled: AutoApplyEnabledInput;
}

interface IAutoApplyTerminalState {
  status: AutoApplyDepositStatus.Applied | AutoApplyDepositStatus.Failed;
  creditStateQueryFailed: boolean;
}

type AutoApplyCredits = (
  invoice: WithRef<IInvoice>,
  appointmentRef: DocumentReference<IAppointment>
) => Promise<unknown>;

export class AutoApplyDepositBloc {
  private readonly _attemptedInvoicePaths = new Set<string>();
  private readonly _terminalStates = new Map<string, IAutoApplyTerminalState>();
  private _activeInvoicePath?: string;

  readonly status = signal<AutoApplyDepositStatus>(
    AutoApplyDepositStatus.Inactive
  );
  readonly creditStateQueryFailed = signal(false);

  readonly paymentActionsReady: Signal<boolean> = computed(() => {
    const status = this.status();
    return (
      status !== AutoApplyDepositStatus.Resolving &&
      status !== AutoApplyDepositStatus.Applying
    );
  });

  readonly progressLabel: Signal<string> = computed(() =>
    this.status() === AutoApplyDepositStatus.Applying
      ? APPLYING_DEPOSIT_LABEL
      : RESOLVING_DEPOSIT_LABEL
  );

  constructor(
    private _applyingCredit: WritableSignal<boolean>,
    private _autoApplyCredits: AutoApplyCredits
  ) {}

  syncGate(gate: IAutoApplyGate): void {
    this._activateInvoice(gate.invoice);
    if (this._isSettled()) {
      return;
    }
    const excluded =
      AppointmentCreditInputs.resolveAutoApplyEligibility(
        gate.appointmentRef,
        gate.autoApplyEnabled
      ) === AutoApplyEligibility.Excluded;
    this.status.set(
      excluded
        ? AutoApplyDepositStatus.Inactive
        : AutoApplyDepositStatus.Resolving
    );
  }

  markCreditStateFailed(invoice: WithRef<IInvoice>): void {
    if (
      this._activeInvoicePath &&
      this._activeInvoicePath !== invoice.ref.path
    ) {
      return;
    }
    this._activateInvoice(invoice);
    if (this._isSettled()) {
      return;
    }
    this._settle(invoice.ref.path, AutoApplyDepositStatus.Failed, true);
    this._applyingCredit.set(false);
  }

  async attempt(attempt: IAutoApplyAttempt): Promise<void> {
    const { invoice, appointmentRef, creditState } = attempt;
    const invoicePath = invoice.ref.path;
    if (
      this._activeInvoicePath !== invoicePath ||
      this._terminalStates.has(invoicePath)
    ) {
      return;
    }
    if (creditState.matchingTotal <= 0) {
      if (this.status() === AutoApplyDepositStatus.Resolving) {
        this.status.set(AutoApplyDepositStatus.Inactive);
      }
      return;
    }
    if (this._attemptedInvoicePaths.has(invoicePath)) {
      return;
    }
    this._attemptedInvoicePaths.add(invoicePath);
    this.status.set(AutoApplyDepositStatus.Applying);
    this._applyingCredit.set(true);
    try {
      await this._autoApplyCredits(invoice, appointmentRef);
      this._settle(invoicePath, AutoApplyDepositStatus.Applied);
    } catch {
      this._settle(invoicePath, AutoApplyDepositStatus.Failed);
    } finally {
      if (this._activeInvoicePath === invoicePath) {
        this._applyingCredit.set(false);
      }
    }
  }

  private _activateInvoice(invoice: WithRef<IInvoice>): void {
    if (this._activeInvoicePath === invoice.ref.path) {
      return;
    }
    this._activeInvoicePath = invoice.ref.path;
    const terminalState = this._terminalStates.get(invoice.ref.path);
    this.status.set(terminalState?.status ?? AutoApplyDepositStatus.Inactive);
    this.creditStateQueryFailed.set(
      terminalState?.creditStateQueryFailed ?? false
    );
    this._applyingCredit.set(false);
  }

  private _settle(
    invoicePath: string,
    status: AutoApplyDepositStatus.Applied | AutoApplyDepositStatus.Failed,
    creditStateQueryFailed: boolean = false
  ): void {
    this._terminalStates.set(invoicePath, {
      status,
      creditStateQueryFailed,
    });
    if (this._activeInvoicePath !== invoicePath) {
      return;
    }
    this.status.set(status);
    this.creditStateQueryFailed.set(creditStateQueryFailed);
  }

  private _isSettled(): boolean {
    const status = this.status();
    return (
      status === AutoApplyDepositStatus.Applying ||
      status === AutoApplyDepositStatus.Applied ||
      status === AutoApplyDepositStatus.Failed
    );
  }
}
